/**
 * Chat Utilities
 * Helper functions for message handling and formatting
 */

import type { ChatMessage, ButtonOption } from '../components/chat/types.js';

/**
 * Returns true if a chat message contains any interactive action blocks (buttons, cards).
 * Shop cards are excluded: they stay interactive regardless of later messages.
 */
export function hasInteractiveActions(message: ChatMessage): boolean {
  return (
    !!message.hierarchy_buttons?.length ||
    !!message.normal_buttons?.length ||
    !!message.list_buttons?.length ||
    !!message.csat?.length ||
    !!message.recipes?.length ||
    !!message.coupons?.length
  );
}

/**
 * Creates a chat message object
 */
export function createChatMessage(
  message: string,
  sender: string,
  direction: 'incoming' | 'outgoing',
  hierarchy_buttons?: ButtonOption[],
  normal_buttons?: ButtonOption[],
  list_buttons?: ButtonOption[]
): ChatMessage {
  return {
    id: `msg_${Date.now()}_${Math.random()}`,
    message,
    sentTime: new Date().toISOString(),
    sender,
    direction,
    hierarchy_buttons,
    normal_buttons,
    list_buttons,
  };
}

/**
 * Converts a server timestamp to the user's local time as an ISO-like string.
 * Stamps without timezone info are treated as UTC (appends 'Z' before parsing).
 */
export function toLocalTimestamp(stamp: string): string {
  const trimmed = stamp.trim();
  if (!trimmed) return stamp;

  // Supports BFF format "dd/MM/yyyy HH:mm:ss" by treating it as UTC.
  const bffMatch = trimmed.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})(?::(\d{2}))?$/
  );

  let date: Date;
  if (bffMatch) {
    const [, day, month, year, hours, minutes, seconds = '0'] = bffMatch;
    date = new Date(Date.UTC(+year, +month - 1, +day, +hours, +minutes, +seconds));
  } else {
    const normalized = trimmed.includes(' ') ? trimmed.replace(' ', 'T') : trimmed;
    const hasTimezone = /(?:Z|[+\-]\d{2}:?\d{2})$/i.test(normalized);
    const utcStamp = hasTimezone ? normalized : `${normalized}Z`;
    date = new Date(utcStamp);
  }

  if (isNaN(date.getTime())) return stamp;
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/**
 * Formats a timestamp to localized time: dd/mm HH:mm
 * Accepts ISO strings or BFF format "dd/MM/yyyy HH:mm:ss".
 */
export function formatMessageTime(isoString: string): string {
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month} ${hours}:${minutes}`;
}

/**
 * Scrolls message list to bottom
 */
export function scrollToBottom(shadowRoot: ShadowRoot | null): void {
  requestAnimationFrame(() => {
    const messageList = shadowRoot?.querySelector('.message-list');
    if (messageList) {
      messageList.scrollTop = messageList.scrollHeight;
    }
  });
}

/**
 * Grows a textarea to fit its content, up to the max-height set in CSS.
 */
export function autoResizeTextarea(textarea: HTMLTextAreaElement): void {
  textarea.style.height = 'auto';
  const style = getComputedStyle(textarea);
  const maxHeight = Number.parseFloat(style.maxHeight) || Infinity;
  const singleLineHeight =
    Number.parseFloat(style.lineHeight) + Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom);
  const contentFits = textarea.scrollHeight <= maxHeight;
  textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  textarea.style.overflowY = contentFits ? 'hidden' : 'auto';
  // Switches to the tighter radius as soon as the textarea grows past its first line.
  textarea.closest('.input-wrapper')?.classList.toggle('input-wrapper--multiline', textarea.scrollHeight > singleLineHeight + 1);
}

/**
 * Updates the last message in the list
 */
export function updateLastMessage(
  messages: ChatMessage[],
  newText: string,
  hierarchy_buttons?: ButtonOption[],
  normal_buttons?: ButtonOption[]
): ChatMessage[] {
  const updatedMessages = [...messages];
  const lastMessage = updatedMessages[updatedMessages.length - 1];
  if (!lastMessage) return updatedMessages;


  updatedMessages[updatedMessages.length - 1] = {
    ...lastMessage,
    message: newText,
    hierarchy_buttons: hierarchy_buttons || lastMessage.hierarchy_buttons,
    normal_buttons: normal_buttons || lastMessage.normal_buttons,
  };
  return updatedMessages;
}

/**
 * Safely reads a property value from an object by field name.
 * Returns the value as a string, or an empty string if not found.
 */
export function getFieldValue<T>(obj: T, field: string): string {
  const value = (obj as Record<string, unknown>)?.[field];
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return `${value}`;
  return JSON.stringify(value);
}

/**
 * Sanitizes URLs to prevent malicious protocols (e.g., javascript:, data:)
 * Only allows http:// and https:// URLs
 */
export function sanitizeUrl(url: string): string {
  if (!url) return '';
  const trimmed = url.trim();
  // Only allow http and https
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return '';
}

/**
 * Posts a ChatMarianaLinks message via window.postMessage for the host app (AppCC).
 * Only called when source is 'appcc' — the caller is responsible for that check.
 */
export function dispatchChatMarianaLinks(url: string): void {
  const safeUrl = sanitizeUrl(url);
  if (!safeUrl) {
    return;
  }

  window.postMessage(
    {
      type: 'ChatMarianaLinks',
      payload: {
        action: 'OPEN_APP_LINK',
        url: safeUrl,
      },
    },
    globalThis.location.origin
  );
}